import type { CardEffectHandlerDeps } from "./handlers/types.ts";
import type { Card } from "../entities/types.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type { EventBus } from "../core/EventBus.ts";
import type { LogicBombState } from "./types.ts";
import { GRID_WIDTH, GRID_HEIGHT, CELL_SIZE } from "../core/config.ts";
import { SELL_MULTIPLIER } from "../systems/EconomySystem.ts";
import {
  gainLives,
  setLives,
  setTimeWarpAvailable,
  setTimeWarpDuration,
  setRecursiveLoop,
  setFirewallSlow,
  setShowAllHPBars,
  setTargetingMode,
  setMaxLives,
  setCascade,
} from "./stateIntents.ts";
import { isTanky } from "../systems/combatUtils.ts";

// Board center in pixels — origin for the board-wide payload detonation FX.
const BOARD_CENTER_X = (GRID_WIDTH * CELL_SIZE) / 2;
const BOARD_CENTER_Y = (GRID_HEIGHT * CELL_SIZE) / 2;

// Gold KERNEL PANIC chain-reaction ("recursiveLoop-Shrapnel"): the 40%-of-current
// blast is non-lethal by construction (floor(hp·0.4) < hp always), so on its own
// gold kills nothing. Instead every trash caught in the blast detonates once,
// spraying a fraction of its own maxHp as lightning to survivors within this
// radius. Single-tier — sources are the snapshot of trash present at detonation,
// so secondary kills never re-explode → the cascade is bounded & deterministic.
const SHRAPNEL_RADIUS = CELL_SIZE * 2;
const SHRAPNEL_FRAC = 0.5;

// ─── Card Effect Definition ────────────────────────────

export type PayloadTargetMode = "global" | "targetEnemy" | "targetCell";

interface CardEffectDef {
  kind: "instant" | "persistent" | "both";
  /** Delivery mode: undefined = immediate (fire on pick); "payload" = armed in rack, deployed later. */
  delivery?: "payload";
  /** For payloads: how the player aims before deploying. */
  targetMode?: PayloadTargetMode;
  apply: (card: Card, ctx: CardEffectHandlerDeps) => void;
  remove?: (card: Card, ctx: CardEffectHandlerDeps) => void;
}

// ─── Single-source dispatch map ────────────────────────

export const CARD_EFFECTS: Record<string, CardEffectDef> = {
  // ─── Instant-only effects ───────────────────────────
  addCredits: {
    kind: "instant",
    apply: (card, ctx) => {
      ctx.economy.addGold(card.effect.value as number);
    },
  },
  revealWaves: {
    kind: "instant",
    apply: (card, ctx) => {
      ctx.eventBus.emit("intel.revealWaves", {
        count: card.effect.value as number,
      });
    },
  },
  kernelPanic: {
    kind: "instant",
    delivery: "payload",
    targetMode: "global",
    apply: (card, ctx) => {
      const bossMult = 0.15;
      const normalMult = card.effect.value as number;
      const list = ctx.waveManager.enemies;
      // Snapshot the trash present at detonation — these are the shrapnel
      // sources. Boss/tank are too armored to chain (gold barely dents them).
      const sources: {
        e: (typeof list)[number];
        x: number;
        y: number;
        maxHp: number;
      }[] = [];
      for (const enemy of list) {
        if (!enemy.active) continue;
        const tanky = isTanky(enemy);
        if (!tanky) {
          sources.push({
            e: enemy,
            x: enemy.x,
            y: enemy.y,
            maxHp: enemy.maxHp,
          });
        }
        const dmg = Math.floor(enemy.hp * (tanky ? bossMult : normalMult));
        if (enemy.takeDamage(dmg, "lightning")) {
          enemy.active = false;
          ctx.eventBus.emit("enemy.killed", { enemy });
        }
      }
      // Single-tier shrapnel cascade: each snapshot source detonates once,
      // spraying floor(maxHp · SHRAPNEL_FRAC) lightning to other active enemies
      // in radius. Secondary kills are NOT added as sources → bounded, no
      // recursion; a source never damages itself.
      const r2 = SHRAPNEL_RADIUS * SHRAPNEL_RADIUS;
      for (const src of sources) {
        ctx.eventBus.emit("payload.shrapnel", { x: src.x, y: src.y });
        const shrapnelDmg = Math.floor(src.maxHp * SHRAPNEL_FRAC);
        if (shrapnelDmg <= 0) continue;
        for (const enemy of list) {
          if (!enemy.active || enemy === src.e) continue;
          const dx = enemy.x - src.x;
          const dy = enemy.y - src.y;
          if (dx * dx + dy * dy > r2) continue;
          if (enemy.takeDamage(shrapnelDmg, "lightning")) {
            enemy.active = false;
            ctx.eventBus.emit("enemy.killed", { enemy });
          }
        }
      }
      // Spectacle is owned by the render-only PayloadDetonationFx (decoupled,
      // determinism-safe) — emit the cue from board center.
      ctx.eventBus.emit("payload.detonated", {
        type: "kernelPanic",
        x: BOARD_CENTER_X,
        y: BOARD_CENTER_Y,
      });
    },
  },
  // Identifier kept as `reduceMaxHp` for contract stability — the registry key
  // and the `payload.detonated` cue type are referenced across FX, comms and
  // tests. The mechanic is the Violet "elite expose": a non-lethal chunk plus a
  // vulnerability-mark on the strongest active enemy CLASS, so towers (which
  // amplify and prefer marked targets) crack the elites gold barely dents.
  // Deterministic, no RNG — target = highest-maxHp active enemy, spawn-order
  // tiebreak; the mark timer is ticked by sim-dt in Enemy.updateStatusEffects.
  reduceMaxHp: {
    kind: "instant",
    delivery: "payload",
    targetMode: "global",
    apply: (card, ctx) => {
      const list = ctx.waveManager.enemies;
      let primary: (typeof list)[number] | undefined;
      for (const en of list) {
        if (!en.active) continue;
        if (!primary || en.maxHp > primary.maxHp) primary = en;
      }
      if (!primary) return;
      const eliteType = primary.type;
      const chunk = card.effect.value as number; // 0.40 → 40% of current HP
      const MARK_BONUS = 0.5; // +50% tower damage on exposed targets
      const MARK_MS = 5000; // 5s exposure window
      for (const en of list) {
        if (!en.active || en.type !== eliteType) continue;
        // Non-lethal: floor at ≥1 HP so the payload never lands the kill itself.
        en.hp = Math.max(1, en.hp - Math.floor(en.hp * chunk));
        en.marked = true;
        en.markDamageBonus = MARK_BONUS;
        en.markTimer = MARK_MS;
      }
      // Implosion cue at the primary (strongest) enemy — render-only FX + Vera.
      ctx.eventBus.emit("payload.detonated", {
        type: "reduceMaxHp",
        x: primary.x,
        y: primary.y,
      });
    },
  },

  // ─── BL-70: New payload effects ─────────────────────

  /** GC --FORCE: delete enemies below threshold HP + refund gold per kill. */
  gcForce: {
    kind: "instant",
    delivery: "payload",
    targetMode: "global",
    apply: (card, ctx) => {
      const threshold = card.effect.value as number; // fraction, e.g. 0.25
      const list = ctx.waveManager.enemies;
      const GOLD_PER_KILL = 5;
      let kills = 0;
      for (const enemy of list) {
        if (!enemy.active) continue;
        if (enemy.hp / enemy.maxHp > threshold) continue;
        enemy.active = false;
        enemy.reachedEnd = false;
        kills++;
        ctx.eventBus.emit("enemy.killed", { enemy });
      }
      if (kills > 0) {
        ctx.economy.addGold(kills * GOLD_PER_KILL);
      }
      ctx.eventBus.emit("payload.detonated", {
        type: "gcForce",
        x: BOARD_CENTER_X,
        y: BOARD_CENTER_Y,
      });
    },
  },

  /** LOGIC_BOMB: plant a delayed charge on a cell. Detonates AoE scaled by cluster. */
  logicBomb: {
    kind: "instant",
    delivery: "payload",
    targetMode: "targetCell",
    apply: (card, ctx) => {
      const target = ctx.payloadTarget;
      if (!target || target.col == null || target.row == null) return;
      const FUSE_MS = 2000;
      ctx.gs.logicBombs.push({
        col: target.col,
        row: target.row,
        timer: FUSE_MS,
        baseDamage: card.effect.value as number,
      });
      ctx.eventBus.emit("payload.detonated", {
        type: "logicBomb",
        x: target.col * CELL_SIZE + CELL_SIZE / 2,
        y: target.row * CELL_SIZE + CELL_SIZE / 2,
      });
    },
  },

  /** BACKDOOR: yank lead N enemies back to spawn (reset path progress). */
  backdoor: {
    kind: "instant",
    delivery: "payload",
    targetMode: "global",
    apply: (card, ctx) => {
      const count = card.effect.value as number; // e.g. 5
      const list = ctx.waveManager.enemies;
      // Sort active enemies by path progress (highest pathIndex = furthest ahead).
      const active = list.filter((e) => e.active && e.path && e.pathIndex > 1);
      active.sort((a, b) => b.pathIndex - a.pathIndex);
      const targets = active.slice(0, count);
      for (const enemy of targets) {
        if (!enemy.path || enemy.path.length === 0) continue;
        const spawn = enemy.path[0]!;
        enemy.pathIndex = 1;
        enemy.x = spawn.x;
        enemy.y = spawn.y;
      }
      ctx.eventBus.emit("payload.detonated", {
        type: "backdoor",
        x: BOARD_CENTER_X,
        y: BOARD_CENTER_Y,
      });
    },
  },

  /** FORK_BOMB: apply contagion DoT to target enemy via the existing virus system. */
  forkBomb: {
    kind: "instant",
    delivery: "payload",
    targetMode: "targetEnemy",
    apply: (card, ctx) => {
      const target = ctx.payloadTarget;
      if (target?.enemyIndex == null) return;
      const enemy = ctx.waveManager.enemies[target.enemyIndex];
      if (!enemy || !enemy.active) return;
      const dps = card.effect.value as number; // damage per tick
      const TICK_INTERVAL = 500;
      const DURATION = 6000;
      enemy.applyDot(dps, DURATION, TICK_INTERVAL, "virus", 0, {
        duration: DURATION,
        spreadRadius: CELL_SIZE * 2,
        spreadChance: 1.0,
        maxGenerations: 4,
        spreadInterval: 800,
        spreadTimer: 0,
        speedDebuff: 0.7,
      });
      ctx.eventBus.emit("payload.detonated", {
        type: "forkBomb",
        x: enemy.x,
        y: enemy.y,
      });
    },
  },

  /** PID_HIJACK: turn target enemy around — it damages others on its way back. */
  pidHijack: {
    kind: "instant",
    delivery: "payload",
    targetMode: "targetEnemy",
    apply: (card, ctx) => {
      const target = ctx.payloadTarget;
      if (target?.enemyIndex == null) return;
      const enemy = ctx.waveManager.enemies[target.enemyIndex];
      if (!enemy || !enemy.active) return;
      const HIJACK_DURATION = 4000;
      const dps = card.effect.value as number;
      enemy.hijackedTimer = HIJACK_DURATION;
      enemy.hijackedDps = dps;
      ctx.eventBus.emit("payload.detonated", {
        type: "pidHijack",
        x: enemy.x,
        y: enemy.y,
      });
    },
  },

  // ─── Both instant and persistent (addLives) ─────────
  addLives: {
    kind: "both",
    apply: (card, ctx) => {
      gainLives(ctx.gs, card.effect.value as number);
    },
    remove: (_card, _ctx) => {
      // addLives grants are permanent — nothing to revoke on removal
    },
  },

  // ─── Persistent effects ─────────────────────────────
  diagonalSynergy: {
    kind: "persistent",
    apply: (_card, ctx) => {
      ctx.synergySystem.allowDiagonal = true;
      ctx.synergySystem.recalculate();
    },
    remove: (_card, ctx) => {
      if (!ctx.draftSystem.hasDiagonalSynergy()) {
        ctx.synergySystem.allowDiagonal = false;
        ctx.synergySystem.recalculate();
      }
    },
  },
  piercingShots: {
    kind: "persistent",
    apply: (card, ctx) => {
      ctx.combatSystem.piercingBonus += card.effect.value as number;
    },
    remove: (card, ctx) => {
      ctx.combatSystem.piercingBonus = Math.max(
        0,
        ctx.combatSystem.piercingBonus - (card.effect.value as number),
      );
    },
  },
  timeWarp: {
    kind: "persistent",
    apply: (card, ctx) => {
      setTimeWarpAvailable(ctx.gs, true);
      setTimeWarpDuration(ctx.gs, card.effect.duration ?? 5);
      ctx.eventBus.emit("game.timeWarpChanged", true);
    },
    remove: (_card, ctx) => {
      setTimeWarpAvailable(ctx.gs, false);
      ctx.eventBus.emit("game.timeWarpChanged", false);
    },
  },
  sellMultiplier: {
    kind: "persistent",
    apply: (card, ctx) => {
      ctx.economy.sellMultiplier = card.effect.value as number;
    },
    remove: (_card, ctx) => {
      ctx.economy.sellMultiplier = SELL_MULTIPLIER;
    },
  },
  synergyFrequency: {
    kind: "persistent",
    apply: (card, ctx) => {
      ctx.synergySystem.frequencyMultiplier = card.effect.value as number;
    },
    remove: (_card, ctx) => {
      ctx.synergySystem.frequencyMultiplier = 1;
    },
  },
  triComboAutoFire: {
    kind: "persistent",
    apply: (_card, ctx) => {
      ctx.synergySystem.triComboAutoFire = true;
    },
    remove: (_card, ctx) => {
      ctx.synergySystem.triComboAutoFire = false;
    },
  },
  elementDamageMultiplier: {
    kind: "persistent",
    // GA-06 fix (2026-gameplay-systems-audit): this used to ALSO write
    // ctx.combatSystem.elementBonuses via applyElementBonus, double-applying
    // the same bonus on top of DraftSystem.getDamageMultiplier()'s own query
    // (a "+20%" card composed to +44%) and never reverting on remove (a ×1
    // multiply is a no-op, not the ÷1.2 needed to undo the apply — the bonus
    // leaked permanently across pick/swap cycles). Handled via
    // DraftSystem.getDamageMultiplier() alone — just needs to be active.
    // elementBonuses stays reserved for operator elemental modifiers
    // (OperatorEffects.ts), which DraftSystem cannot see.
    // (params omitted: no-op handlers, delivered via the query path; keeps the
    //  lint ratchet tight rather than adding unused-arg warnings.)
    apply: () => {
      // Handled via DraftSystem.getDamageMultiplier() — just needs to be active
    },
    remove: () => {
      // Recalculated dynamically via DraftSystem.getDamageMultiplier()
    },
  },
  killGoldMultiplier: {
    kind: "persistent",
    apply: (_card, _ctx) => {
      // Handled via DraftSystem.getKillGoldMultiplier() — just needs to be active
    },
    remove: (_card, _ctx) => {
      // Recalculated dynamically via DraftSystem.getKillGoldMultiplier()
    },
  },
  globalDamageMultiplier: {
    kind: "persistent",
    apply: (card, ctx) => {
      ctx.combatSystem.globalDamageMult *= card.effect.value as number;
    },
    remove: (card, ctx) => {
      const baseDmg = ctx.moduleBaselines?.globalDamageMult ?? 1;
      ctx.combatSystem.globalDamageMult /= card.effect.value as number;
      if (Math.abs(ctx.combatSystem.globalDamageMult - baseDmg) < 0.001) {
        ctx.combatSystem.globalDamageMult = baseDmg;
      }
    },
  },
  globalFireRateMultiplier: {
    kind: "persistent",
    apply: (_card, _ctx) => {
      // Handled via DraftSystem.getGlobalFireRateMultiplier()
    },
    remove: (_card, _ctx) => {
      // Recalculated dynamically via DraftSystem.getGlobalFireRateMultiplier()
    },
  },
  globalRangeMultiplier: {
    kind: "persistent",
    apply: (card, ctx) => {
      ctx.combatSystem.rangeMultiplier *= card.effect.value as number;
    },
    remove: (card, ctx) => {
      const baseRange = ctx.moduleBaselines?.rangeMultiplier ?? 1;
      ctx.combatSystem.rangeMultiplier /= card.effect.value as number;
      if (Math.abs(ctx.combatSystem.rangeMultiplier - baseRange) < 0.001) {
        ctx.combatSystem.rangeMultiplier = baseRange;
      }
    },
  },
  globalSlow: {
    kind: "persistent",
    apply: (_card, _ctx) => {
      // Handled via DraftSystem.hasGlobalSlow() on enemy spawn
    },
    remove: (_card, _ctx) => {
      // Recalculated dynamically via DraftSystem.hasGlobalSlow()
    },
  },
  bossDamageMultiplier: {
    kind: "persistent",
    apply: (card, ctx) => {
      ctx.combatSystem.bossDamageReduction *= card.effect.value as number;
    },
    remove: (card, ctx) => {
      const baseBoss = ctx.moduleBaselines?.bossDamageReduction ?? 1;
      ctx.combatSystem.bossDamageReduction /= card.effect.value as number;
      if (Math.abs(ctx.combatSystem.bossDamageReduction - baseBoss) < 0.001) {
        ctx.combatSystem.bossDamageReduction = baseBoss;
      }
    },
  },
  draftCountBonus: {
    kind: "persistent",
    apply: (card, ctx) => {
      ctx.draftSystem.drawCount =
        ctx.draftSystem.drawCount + (card.effect.value as number);
    },
    remove: (card, ctx) => {
      ctx.draftSystem.drawCount = Math.max(
        3,
        ctx.draftSystem.drawCount - (card.effect.value as number),
      );
    },
  },
  recursiveLoop: {
    kind: "persistent",
    apply: (card, ctx) => {
      setRecursiveLoop(ctx.gs, card.effect.value as number);
    },
    remove: (_card, ctx) => {
      setRecursiveLoop(ctx.gs, 0);
    },
  },
  firewallSlow: {
    kind: "persistent",
    apply: (card, ctx) => {
      setFirewallSlow(ctx.gs, true, card.effect.value as number);
    },
    remove: (_card, ctx) => {
      setFirewallSlow(ctx.gs, false, 1.0);
    },
  },
  showAllHp: {
    kind: "persistent",
    apply: (_card, ctx) => {
      setShowAllHPBars(ctx.gs, true);
    },
    remove: (_card, ctx) => {
      setShowAllHPBars(ctx.gs, false);
    },
  },
  targetFastest: {
    kind: "persistent",
    apply: (_card, ctx) => {
      setTargetingMode(ctx.gs, "fastest");
      ctx.combatSystem.targetFastest = true;
    },
    remove: (_card, ctx) => {
      setTargetingMode(ctx.gs, "default");
      ctx.combatSystem.targetFastest = false;
    },
  },

  // ─── Trade-off cards (B2) ───────────────────────────

  /** GREED_PROTOCOL: +60% kill gold / −20% damage. Uses existing query levers. */
  greedProtocol: {
    kind: "persistent",
    apply: (_card, _ctx) => {
      // Both sides handled via DraftSystem queries:
      // getKillGoldMultiplier reads the value; getGreedDamagePenalty reads the penalty.
    },
    remove: (_card, _ctx) => {
      // Recalculated dynamically.
    },
  },

  /** MINIMALIST: +15% global dmg per empty skill slot. Query-based. */
  minimalist: {
    kind: "persistent",
    apply: (_card, _ctx) => {
      // Handled via DraftSystem.getMinimalistBonus()
    },
    remove: (_card, _ctx) => {
      // Recalculated dynamically.
    },
  },

  /** GLASS_PROTOCOL: +60% global dmg / −2 max integrity. */
  glassProtocol: {
    kind: "persistent",
    apply: (card, ctx) => {
      ctx.combatSystem.globalDamageMult *= card.effect.value as number;
      const penalty = card.effect.penalty ?? 2;
      setLives(ctx.gs, Math.max(1, ctx.gs.lives - penalty));
      setMaxLives(ctx.gs, Math.max(1, (ctx.gs.maxLives ?? 20) - penalty)); // F10 floor
    },
    remove: (card, ctx) => {
      ctx.combatSystem.globalDamageMult /= card.effect.value as number;
      const penalty = card.effect.penalty ?? 2;
      setMaxLives(ctx.gs, (ctx.gs.maxLives ?? 20) + penalty);
      // F9: also restore the integrity the card cost (maxLives is restored
      // above; clamp lives to it). Without this, swapping GLASS_PROTOCOL out
      // permanently drained ~2 lives — apply removed lives but remove never
      // gave them back.
      setLives(ctx.gs, Math.min(ctx.gs.maxLives, ctx.gs.lives + penalty));
    },
  },

  /** SCORCHED_EARTH: +30% global dmg / kill gold halved. */
  scorchedEarth: {
    kind: "persistent",
    apply: (card, ctx) => {
      ctx.combatSystem.globalDamageMult *= card.effect.value as number;
      // Penalty handled via DraftSystem.getScorchedEarthPenalty()
    },
    remove: (card, ctx) => {
      ctx.combatSystem.globalDamageMult /= card.effect.value as number;
    },
  },

  // ─── Synergy-payoff cards (B4) ──────────────────────

  /** RESONANCE: +12% global dmg per active synergy. Query-based. */
  resonance: {
    kind: "persistent",
    apply: (_card, _ctx) => {
      // Handled via DraftSystem.getResonanceBonus() reading synergySystem.activeSynergies.length
    },
    remove: (_card, _ctx) => {
      // Recalculated dynamically.
    },
  },

  /** CASCADE: synergy triggers splash AoE damage. Event-driven. */
  cascade: {
    kind: "persistent",
    apply: (_card, ctx) => {
      setCascade(ctx.gs, true, 25);
    },
    remove: (_card, ctx) => {
      setCascade(ctx.gs, false, 0);
    },
  },

  // ─── Build-define legendaries (B3) ──────────────────

  /** APEX_PREDATOR: dominant element +120% / others −40%. Query-based. */
  apexPredator: {
    kind: "persistent",
    apply: (_card, _ctx) => {
      // Handled via DraftSystem.getApexPredatorMultiplier(element)
    },
    remove: (_card, _ctx) => {
      // Recalculated dynamically.
    },
  },

  /** MONOCULTURE: all towers count as all elements for synergy / −25% global dmg. */
  monoculture: {
    kind: "persistent",
    apply: (card, ctx) => {
      ctx.synergySystem.monocultureEnabled = true;
      ctx.synergySystem.recalculate();
      const penalty = card.effect.penalty ?? 0.75;
      ctx.combatSystem.globalDamageMult *= penalty;
    },
    remove: (card, ctx) => {
      ctx.synergySystem.monocultureEnabled = false;
      ctx.synergySystem.recalculate();
      const penalty = card.effect.penalty ?? 0.75;
      ctx.combatSystem.globalDamageMult /= penalty;
    },
  },

  // ─── Late-game legendaries (B5) ─────────────────────

  /** DEAD_MAN'S_SWITCH: 2× dmg when lives < threshold. Query-based. */
  deadMansSwitch: {
    kind: "persistent",
    apply: (_card, _ctx) => {
      // Handled via DraftSystem.getDeadMansSwitchMultiplier(currentLives)
    },
    remove: (_card, _ctx) => {
      // Recalculated dynamically.
    },
  },
};

// ─── Derived sets (single source — no hand-maintained lists) ───

/** Card effect.type values handled as instant (one-shot, never occupy a slot). */
export const INSTANT_CARD_EFFECTS: ReadonlySet<string> = new Set(
  Object.entries(CARD_EFFECTS)
    .filter(([, d]) => d.kind === "instant" || d.kind === "both")
    .map(([k]) => k),
);

/** Card effect.type values handled as persistent (apply + remove). */
export const PERSISTENT_CARD_EFFECTS: ReadonlySet<string> = new Set(
  Object.entries(CARD_EFFECTS)
    .filter(([, d]) => d.kind === "persistent" || d.kind === "both")
    .map(([k]) => k),
);

/** Card effect.type values that are PAYLOADS (armed in rack, deployed later). */
export const PAYLOAD_CARD_EFFECTS: ReadonlySet<string> = new Set(
  Object.entries(CARD_EFFECTS)
    .filter(([, d]) => d.delivery === "payload")
    .map(([k]) => k),
);

/** Check whether a card is a payload (armed in rack, player deploys later). */
export function isPayloadCard(card: Card): boolean {
  return (
    card.effect.target === "instant" &&
    PAYLOAD_CARD_EFFECTS.has(card.effect.type)
  );
}

/** Get the targeting mode for a payload card (undefined if not a payload). */
export function getPayloadTargetMode(
  card: Card,
): PayloadTargetMode | undefined {
  if (!isPayloadCard(card)) return undefined;
  return CARD_EFFECTS[card.effect.type]?.targetMode ?? "global";
}

// ─── Dispatchers ───────────────────────────────────────

/** Apply a drafted card's effect to game systems. */
export function applyCardEffect(card: Card, ctx: CardEffectHandlerDeps): void {
  const e = card.effect;

  // ─── Instant effects (never occupy a slot) ───────────────────
  if (e.target === "instant") {
    applyInstantEffect(card, ctx);
    return;
  }

  // ─── Persistent / Temp effects ───────────────────────────────
  const def = CARD_EFFECTS[e.type];
  if (def) {
    def.apply(card, ctx);
  }
}

/** Handle instant effects that don't occupy a slot. */
function applyInstantEffect(card: Card, ctx: CardEffectHandlerDeps): void {
  const def = CARD_EFFECTS[card.effect.type];
  if (def) {
    def.apply(card, ctx);
  }
}

/** Remove a card's effect (on expiry or swap). */
export function removeCardEffect(card: Card, ctx: CardEffectHandlerDeps): void {
  const e = card.effect;

  // Instant cards are never stored — nothing to remove
  if (e.target === "instant") return;

  const def = CARD_EFFECTS[e.type];
  if (def?.remove) {
    def.remove(card, ctx);
  }
}

// ─── Per-frame tick functions (dormant = no-op) ─────────────────────────────

/** LOGIC_BOMB radius for cluster count (in pixels). */
const LOGIC_BOMB_RADIUS = CELL_SIZE * 1.5;

/**
 * Tick all active logic bombs. Dormant (empty array) = strict no-op.
 * Called once per sim frame from the update loop.
 */
export function tickLogicBombs(
  dt: number,
  bombs: LogicBombState[],
  enemies: Enemy[],
  eventBus: EventBus,
): void {
  if (bombs.length === 0) return;
  for (let i = bombs.length - 1; i >= 0; i--) {
    const bomb = bombs[i]!;
    bomb.timer -= dt;
    if (bomb.timer > 0) continue;
    // Detonate: count cluster, deal scaled AoE
    const cx = bomb.col * CELL_SIZE + CELL_SIZE / 2;
    const cy = bomb.row * CELL_SIZE + CELL_SIZE / 2;
    const r2 = LOGIC_BOMB_RADIUS * LOGIC_BOMB_RADIUS;
    let cluster = 0;
    for (const enemy of enemies) {
      if (!enemy.active) continue;
      const dx = enemy.x - cx;
      const dy = enemy.y - cy;
      if (dx * dx + dy * dy <= r2) cluster++;
    }
    const dmg = Math.floor(bomb.baseDamage * Math.max(1, cluster));
    for (const enemy of enemies) {
      if (!enemy.active) continue;
      const dx = enemy.x - cx;
      const dy = enemy.y - cy;
      if (dx * dx + dy * dy > r2) continue;
      if (enemy.takeDamage(dmg, "lightning")) {
        enemy.active = false;
        eventBus.emit("enemy.killed", { enemy });
      }
    }
    eventBus.emit("payload.detonated", {
      type: "logicBomb_explode",
      x: cx,
      y: cy,
    });
    bombs.splice(i, 1);
  }
}

/**
 * Tick hijacked enemies: deal proximity damage to non-hijacked neighbors.
 * Dormant (no hijacked enemies) = strict no-op (early exit after one loop).
 * Called once per sim frame from the update loop.
 */
export function tickHijackedDamage(
  dt: number,
  enemies: Enemy[],
  eventBus: EventBus,
): void {
  const HIJACK_RADIUS = CELL_SIZE;
  const r2 = HIJACK_RADIUS * HIJACK_RADIUS;
  for (const hijacked of enemies) {
    if (!hijacked.active || hijacked.hijackedTimer <= 0) continue;
    const dps = hijacked.hijackedDps;
    const dmg = dps * (dt / 1000);
    if (dmg <= 0) continue;
    for (const other of enemies) {
      if (other === hijacked || !other.active || other.hijackedTimer > 0)
        continue;
      const dx = other.x - hijacked.x;
      const dy = other.y - hijacked.y;
      if (dx * dx + dy * dy > r2) continue;
      if (other.takeDamage(dmg)) {
        other.active = false;
        eventBus.emit("enemy.killed", { enemy: other });
      }
    }
  }
}
